import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import * as colors from 'lib/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import AvatarIcon from 'ui-library/icons/AvatarIcon';

import {AvatarShape, AvatarSize} from './AvatarConstants';
import AvatarFrame, {AvatarFrameType} from './AvatarFrame';
import AvatarText from './AvatarText';

const AVATAR_DEFAULT_IMG = 'no_avatar.jpg';

export const NO_AVATAR_URL = `/static/img/${AVATAR_DEFAULT_IMG}`;

export interface HasAvatar {
  avatar_url?: string | null;
  avatarUrl?: string | null;
  email?: string;
  handle?: string;
  name?: string;
}

export interface AvatarProps {
  className?: string;
  size: AvatarSize;
  shape: AvatarShape;
  entity: HasAvatar;
  showTooltip: boolean;
  frame?: AvatarFrameType | null;
}

export function hasDefaultAvatar(hasAvatar: HasAvatar): boolean {
  return hasAvatar.avatar_url === NO_AVATAR_URL || hasAvatar.avatarUrl === NO_AVATAR_URL;
}

/**
 * An avatar based on an entity's avatar image or an identifier (handle, name, or email) if no image exists.
 */
class Avatar extends React.Component<AvatarProps> {
  // Avatar initials will be extracted from the handle, name, or email.
  static getEntityIdentifier(entity: HasAvatar): string {
    const identifier = entity.name || entity.handle || entity.email;
    assert(identifier != null, 'Avatar entity must have name, handle, or email specified.');
    return identifier;
  }
  static getHeight(size: AvatarSize): number {
    // Keep in sync w/ less/components/avatars.less
    switch (size) {
      case 'xxs':
        return 16;
      case 'xs':
        return 20;
      case 'small':
        return 28;
      case 'newsmall':
        return 28;
      case 'large':
        return 64;
      default:
        assert(size === 'xl', 'Unexpected size');
        return 84;
    }
  }
  static getInitials(entity: HasAvatar): string {
    // Use initials if the entity has two parts.
    if (entity.name) {
      const nameParts = entity.name.split(' ');
      if (nameParts.length === 2) {
        return nameParts.map((np) => np.charAt(0).toUpperCase()).join('');
      }
    }
    // Otherwise use the first letter of entity's name, handle, or email.
    const entityIdentifier = Avatar.getEntityIdentifier(entity);
    return entityIdentifier.charAt(0).toUpperCase();
  }

  static defaultProps = {
    shape: 'circle',
    showTooltip: false,
  };

  render(): JSX.Element {
    const avatarlUrl = this.props.entity.avatarUrl ?? this.props.entity.avatar_url;
    return (
      <div className={classNames(css(styles.avatarContainer))}>
        <Tooltip
          isDisabled={!this.props.showTooltip}
          text={Avatar.getEntityIdentifier(this.props.entity)}
          placement={FloatingPlacement.TOP}
        >
          <>
            {!avatarlUrl || avatarlUrl.endsWith(AVATAR_DEFAULT_IMG) ? (
              <AvatarText
                backgroundColor={colors.stringToColor(Avatar.getEntityIdentifier(this.props.entity), 0.3)}
                className={this.props.className}
                size={this.props.size}
                shape={this.props.shape}
                text={Avatar.getInitials(this.props.entity)}
              />
            ) : (
              <AvatarIcon
                avatarUrl={avatarlUrl}
                className={this.props.className}
                size={this.props.size}
                shape={this.props.shape}
              />
            )}
            {this.props.frame != null && (
              <AvatarFrame type={this.props.frame} size={this.props.size} shape={this.props.shape} />
            )}
          </>
        </Tooltip>
      </div>
    );
  }
}

export default Avatar;

const styles = StyleSheet.create({
  avatarContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    verticalAlign: 'middle',
  },
});
